import { useLocation } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import { useI18n } from '../../i18n/LanguageContext';
import { resolveLandingContext } from './regional';

type PageContent = {
  title: string;
  subtitle: string;
  sectionTitle: string;
  sectionBody: string;
  sectionDetail: string;
  imageAlt: string;
  capabilitiesTitle: string;
  capabilities: Array<{ title: string; body: string }>;
};

const CONTENT: Record<'default' | 'es', PageContent> = {
  default: {
    title: 'Healthcare Analytics',
    subtitle: 'Operational and financial intelligence to manage clinical productivity, demand, and revenue cycle execution.',
    sectionTitle: 'Clinical and Business KPIs in One View',
    sectionBody: 'Track volume, efficiency, quality, and billing performance for faster leadership decisions.',
    sectionDetail: 'Ideal for multi-site organizations requiring visibility by location and service line.',
    imageAlt: 'Clinical and operational analytics dashboard',
    capabilitiesTitle: 'Core Capabilities',
    capabilities: [
      { title: 'Operational Performance', body: 'Monitor occupancy, throughput, and response times by team and location.' },
      { title: 'Financial Visibility', body: 'Track revenue, outstanding balances, and billing friction on live operational data.' },
      { title: 'Repeatable Reporting', body: 'Configure repeatable reports and dashboards for recurring operational reviews.' },
    ],
  },
  es: {
    title: 'Analitica en Salud en Espanol',
    subtitle: 'Inteligencia operativa y financiera para clinicas y hospitales con foco en productividad, control y seguimiento ejecutivo.',
    sectionTitle: 'KPI Clinicos y Financieros en un Solo Panel',
    sectionBody: 'Monitorea demanda asistencial, calidad, eficiencia operativa y desempeno del ciclo de ingresos.',
    sectionDetail: 'Disenado para organizaciones multi-sede que necesitan decisiones rapidas por servicio y ubicacion.',
    imageAlt: 'Panel de analitica en salud en espanol',
    capabilitiesTitle: 'Capacidades para Escala Operativa',
    capabilities: [
      { title: 'Rendimiento Operativo', body: 'Controla volumen, throughput y tiempos de respuesta por equipo y sede.' },
      { title: 'Visibilidad Financiera', body: 'Supervisa ingresos, pendientes y fricciones de facturacion en datos reales de operacion.' },
      { title: 'Reporteria Repetible', body: 'Estandariza reportes y tableros para seguimiento ejecutivo semanal y mensual.' },
    ],
  },
};

export default function HealthcareAnalyticsPage() {
  const location = useLocation();
  const { language } = useI18n();
  const { market } = resolveLandingContext(location.pathname, language);
  const content = CONTENT[market] ?? CONTENT.default;

  return (
    <LandingLayout pageKey="healthcare-analytics">
      <section className="tech-header">
        <div className="container">
          <h1 className="reveal-text">{content.title}</h1>
          <p className="tech-features-intro">{content.subtitle}</p>
        </div>
      </section>

      <section className="deep-dive">
        <div className="container deep-dive-grid">
          <div className="dd-content">
            <h2>{content.sectionTitle}</h2>
            <p>{content.sectionBody}</p>
            <p className="tech-feature-detail">{content.sectionDetail}</p>
          </div>
          <div className="dd-image">
            <img src="/emr/dashboard.png" alt={content.imageAlt} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <h2>{content.capabilitiesTitle}</h2>
          <div className="features-grid">
            {content.capabilities.map((item) => (
              <div key={item.title} className="feature-item">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
